/**
 * Cookie-consent storage — the gate that decides whether analytics may ever
 * be initialised (see analytics.ts / AppWithConfig.tsx).
 *
 * This app has real paying customers AND public guest-facing pages (RSVP
 * links a guest opens with no account), which is why this repo does NOT
 * follow enderlane's reference implementation of unconditional PostHog
 * init — see the big comment in createAnalyticsClient.ts. A visitor's
 * choice is persisted in localStorage under CONSENT_STORAGE_KEY so it
 * survives a reload without asking again, and is read synchronously (no
 * network round trip) so the gate can be evaluated on first render.
 */
export type ConsentStatus = "accepted" | "declined" | "unanswered";

export const CONSENT_STORAGE_KEY = "analytics-consent";

/**
 * Reads the stored choice. Absent-tolerant: a private window, cleared site
 * data, or localStorage being unavailable all read as "unanswered" rather
 * than throwing — the banner is the safe default when we cannot tell what a
 * visitor already decided.
 */
export function getStoredConsent(): ConsentStatus {
  try {
    const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (value === "accepted" || value === "declined") return value;
    return "unanswered";
  } catch {
    return "unanswered";
  }
}

export function setStoredConsent(status: "accepted" | "declined"): void {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, status);
  } catch {
    // Swallow — a visitor whose browser blocks storage should still be able
    // to use the product; they will simply be asked again next visit.
  }
}

/** Withdraws consent — the visitor's "change my cookie preferences" escape hatch. */
export function clearStoredConsent(): void {
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Swallow, same reasoning as setStoredConsent.
  }
}
